/**
 * PO print layout — UI only. Bố cục theo mẫu stitch `don hang`,
 * dữ liệu từ PurchaseOrder + Supplier (proto), không sửa business logic.
 *
 * TODO (cột phụ theo loại hàng): chưa có quyết định mapping
 * materialId/spec → cột đặc thù (vd "DÁN VENEER"). Chỉ bảng cột chuẩn ở đây.
 */
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState, type ReactNode } from 'react'
import { Printer, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { COMPANY_PROFILE } from '@/config/companyProfile'
import {
  getPurchaseOrder,
  poDtoToUi,
  money,
  type PurchaseOrderDto,
  type PoLine,
  type PurchaseOrder,
  type ActorRole,
} from '@/services/procurement'
import { supplierService, type Supplier } from '@/services/suppliers'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import { formatSheetAmount } from '@/lib/sheetAmount'
import { splitSpecColor } from '@/features/purchasing/specColor'
import { poSheetColumnValue, poSheetColumnsOf } from '@/features/purchasing/poSheetColumns'
import { formatDateFull } from '@/lib/dateFormat'
import { materialService, type Material } from '@/services/materials'
import { getMaterialRequirementBatchApi, getPoLineTraceabilityApi, type MaterialRequirementBatchRow, type PoLineTraceabilityResult } from '@/services/procurement/materialRequirementApiClient'
import { assemblyTypeLabel } from '@/lib/productConfigLabels'

function formatDateVi(iso: string): string {
  if (!iso) return '—'
  return formatDateFull(iso)
}

export function formatQc(line: PoLine): string {
  if (line.priceSnapshot?.sheet) {
    const columns = poSheetColumnsOf([line]).filter((column) => !/^(màu|màu sắc|color|colour|đv đơn giá)$/i.test(column.name.trim()))
    const dimensions = columns.filter((column) => /^KT\s*\d+$/i.test(column.name))
      .map((column) => poSheetColumnValue(line, column.name)).filter(Boolean)
    const density = columns.filter((column) => /^(định lượng|density)$/i.test(column.name.trim()))
      .map((column) => poSheetColumnValue(line, column.name)).find(Boolean) ?? ''
    if (dimensions.length || density) return [dimensions.join('x'), density].filter(Boolean).join(' · ')
    const values = columns.map((column) => poSheetColumnValue(line, column.name)).filter(Boolean)
    return values.join(' · ') || '—'
  }
  const sc = splitSpecColor(line.spec, line.spec_attributes)
  const labeledKts = [...sc.spec.matchAll(/KT\s*\d+\s*[:=]?\s*([^\s·]+)/gi)].map((match) => match[1] ?? '')
  const density = String(line.spec_attributes?.density ?? sc.spec.match(/(?:định lượng|density)\s*[:=]?\s*([^·]+)/i)?.[1] ?? '').trim()
  const spec = labeledKts.length
    ? [labeledKts.join('x'), density].filter(Boolean).join(' · ')
    : sc.spec.replace(/^(?:quy cách|spec)\s*/i, '').trim()
  return spec || '—'
}

export function formatMaterialColor(line: PoLine): string {
  if (line.priceSnapshot?.sheet) {
    const colorColumn = poSheetColumnsOf([line]).find((column) => /^(màu|màu sắc|color|colour)$/i.test(column.name.trim()))
    if (colorColumn) return poSheetColumnValue(line, colorColumn.name) || '—'
    const snapshotColor = line.priceSnapshot.input.color ?? line.priceSnapshot.sheet.inputs?.color ?? line.priceSnapshot.sheet.mappings?.color
    if (snapshotColor != null && String(snapshotColor).trim()) return String(snapshotColor).trim()
  }
  const capturedColor = line.priceSnapshot?.input.color
  if (capturedColor != null && String(capturedColor).trim()) return String(capturedColor).trim()
  const parsed = splitSpecColor(line.spec, line.spec_attributes)
  const value = parsed.color || line.spec_attributes?.color || line.spec_attributes?.mau
  return String(value ?? '').trim() || '—'
}

export type PoPrintTrace = { productCode: string; productColor: string; assembly: string }

export function buildPoPrintTraceByLineId(
  lines: PoLine[],
  traceResults: PoLineTraceabilityResult[],
  batches: MaterialRequirementBatchRow[],
): Map<string, PoPrintTrace> {
  const resultByLine = new Map(traceResults.map((result) => [result.po_line_id, result]))
  const batchById = new Map(batches.map((batch) => [batch.id, batch]))
  const traces = new Map<string, PoPrintTrace>()
  for (const line of lines) {
    const trace = resultByLine.get(line.id)
    if (!trace) continue
    const snapshot = batchById.get(trace.batch_id)?.input_snapshot ?? []
    const products = trace.breakdown.map((item) => {
      const input = snapshot.find((candidate) => candidate.product_id === item.product_id)
      return {
        productCode: item.product_code.trim(),
        productColor: String(input?.attributes.color ?? '').trim(),
        assembly: assemblyTypeLabel(input?.attributes.assembly_type),
      }
    })
    const distinct = (key: keyof (typeof products)[number]) => [...new Set(products.map((product) => product[key]).filter(Boolean))].join(', ')
    traces.set(line.id, { productCode: distinct('productCode'), productColor: distinct('productColor'), assembly: distinct('assembly') })
  }
  return traces
}

type VatKnownBucket = {
  rate: number
  base: number
  vatAmount: number
  lineCount: number
}

export type PoVatSummary = {
  headerRate: number | null
  subtotal: number
  qtyTotal: number
  knownBuckets: VatKnownBucket[]
  unknownLineCount: number
  knownVatTotal: number
  payableKnown: number
}

export function summarizePoVat(lines: PoLine[], header?: { vat_rate?: number | string | null; vatTotal?: number; grandTotal?: number }): PoVatSummary {
  const headerRate = header?.vat_rate == null ? null : Number(header.vat_rate)
  if (headerRate != null && Number.isFinite(headerRate)) {
    const subtotal = lines.reduce((sum, line) => sum + line.lineTotal, 0)
    const headerVatTotal = header?.vatTotal
    const headerGrandTotal = header?.grandTotal
    const vatAmount = Number.isFinite(headerVatTotal) ? headerVatTotal! : Math.round((subtotal * headerRate / 100 + Number.EPSILON) * 100) / 100
    const payable = Number.isFinite(headerGrandTotal) ? headerGrandTotal! : subtotal + vatAmount
    return {
      headerRate,
      subtotal,
      qtyTotal: lines.reduce((sum, line) => sum + line.qty, 0),
      knownBuckets: [{ rate: headerRate, base: subtotal, vatAmount, lineCount: lines.length }],
      unknownLineCount: 0,
      knownVatTotal: vatAmount,
      payableKnown: payable,
    }
  }
  let subtotal = 0
  let qtyTotal = 0
  let unknownLineCount = 0
  const byRate = new Map<number, VatKnownBucket>()

  for (const line of lines) {
    subtotal += line.lineTotal
    qtyTotal += line.qty
    if (line.vatRateStatus === 'UNKNOWN') {
      unknownLineCount += 1
      continue
    }
    const rate = line.vat ?? 0
    const cur = byRate.get(rate) ?? {
      rate,
      base: 0,
      vatAmount: 0,
      lineCount: 0,
    }
    cur.base += line.lineTotal
    cur.vatAmount += line.lineTotal * (rate / 100)
    cur.lineCount += 1
    byRate.set(rate, cur)
  }

  const knownBuckets = [...byRate.values()].sort((a, b) => a.rate - b.rate)
  const knownVatTotal = knownBuckets.reduce((s, b) => s + b.vatAmount, 0)
  return {
    headerRate: null,
    subtotal,
    qtyTotal,
    knownBuckets,
    unknownLineCount,
    knownVatTotal,
    payableKnown: subtotal + knownVatTotal,
  }
}

export function formatFreePrintValue(row: Record<string, any>, column: { key: string; type: string }): string {
  const raw = row[column.key] ?? row.extra?.[column.key]
  if (raw == null || raw === '') return ''
  if (column.type === 'number') {
    const value = Number(raw)
    return Number.isFinite(value) ? formatSheetAmount(value) : String(raw)
  }
  if (column.type === 'currency') {
    const value = Number(raw)
    return Number.isFinite(value) ? formatSheetAmount(value) : String(raw)
  }
  return String(raw)
}

export function signerForRole(
  po: PurchaseOrder,
  role: ActorRole,
): { name: string; note: string } | null {
  if (po.approvedByRole === role && po.approvedBy) {
    return {
      name: po.approvedBy,
      note: po.approvedAt
        ? `Đã duyệt ${formatDateVi(po.approvedAt)}`
        : 'Đã duyệt',
    }
  }
  return null
}

type PoPrintViewProps = {
  po: PurchaseOrder
  supplier: Supplier | null
  traceByPoLineId?: ReadonlyMap<string, PoPrintTrace>
}

export function PoPrintView({ po, supplier, traceByPoLineId }: PoPrintViewProps) {
  const navigate = useNavigate()
  const company = COMPANY_PROFILE
  const signers = po.signers?.length ? po.signers : [{ title: 'BP ĐẶT HÀNG', name: '' }, { title: 'KẾ HOẠCH', name: '' }, { title: 'BP KHO', name: '' }, { title: 'BP KẾ TOÁN', name: '' }, { title: 'GIÁM ĐỐC', name: '' }]
  const vat = summarizePoVat(po.lines, po)
  const showTraceColumns = po.lines.some((line) => traceByPoLineId?.has(line.id))
  const colCount = showTraceColumns ? 12 : 9
  const printColumns = showTraceColumns
    ? [
        { key: 'row-number', width: '4%' },
        { key: 'product-code', width: '8%' },
        { key: 'product-color', width: '7%' },
        { key: 'assembly', width: '8%' },
        { key: 'product-name', width: '22%', minWidth: '220px' },
        { key: 'spec', width: '12%' },
        { key: 'material-color', width: '5%' },
        { key: 'uom', width: '4%' },
        { key: 'quantity', width: '6%' },
        { key: 'unit-price', width: '8%' },
        { key: 'line-total', width: '9%' },
        { key: 'note', width: '7%' },
      ]
    : [
        { key: 'row-number', width: '4%' },
        { key: 'product-name', width: '30%', minWidth: '220px' },
        { key: 'spec', width: '16%' },
        { key: 'material-color', width: '7%' },
        { key: 'uom', width: '5%' },
        { key: 'quantity', width: '7%' },
        { key: 'unit-price', width: '10%' },
        { key: 'line-total', width: '11%' },
        { key: 'note', width: '10%' },
      ]


  return (
    <div className="font-sans text-slate-800 antialiased po-print-root">
      <style>{`
        @page po-landscape {
          size: A4 landscape;
          margin: 10mm;
        }
        .po-print-root { page: po-landscape; }
        @media print {
          aside, header { display: none !important; }
          .no-print { display: none !important; }
          body, html {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
            font-family: var(--font-sans), 'Inter', sans-serif !important;
          }
          main {
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          main > div {
            padding: 0 !important;
            margin: 0 !important;
          }
          .page-sheet {
            box-shadow: none !important;
            border: none !important;
            max-width: 100% !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .po-print-root input, .po-print-root textarea {
            border: 0 !important; box-shadow: none !important; background: transparent !important; color: inherit !important;
          }
          .po-print-root input::placeholder, .po-print-root textarea::placeholder { color: transparent !important; }
          .po-print-root .po-print-signatures { width: 100% !important; }
          .po-print-root .po-print-signatures > div { min-width: 0 !important; }
          .po-print-root .po-print-notes,
          .po-print-root .po-print-signatures { break-inside: avoid; page-break-inside: avoid; }
          .po-print-root .po-print-table { width: 100% !important; table-layout: fixed !important; }
          .po-print-root .po-print-table th,
          .po-print-root .po-print-table td { white-space: normal !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
          .po-print-root .po-free-print-table .po-free-amount { white-space: nowrap !important; overflow-wrap: normal !important; word-break: normal !important; }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
        .po-print-table th,
        .po-print-table td {
          border: 1px solid #1f2937;
          padding: 5px 8px;
        }
        .po-print-table th {
          background: #fff;
          font-weight: 600;
          text-align: center;
          vertical-align: middle;
          font-size: 13px;
        }
      `}</style>

      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/purchasing/po/${po.id}`)}
        >
          <ArrowLeft className="h-4 w-4" />
          Về chi tiết PO
        </Button>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4" />
          In Đơn Hàng
        </Button>
      </div>

      <main className="page-sheet mx-auto max-w-6xl border border-slate-300 bg-white p-6 text-black shadow-lg sm:p-10">
        {/* Header */}
        <div className="po-print-header grid grid-cols-12 gap-3 border-b border-gray-300 pb-2">
          <div className="col-span-8 flex flex-row items-start gap-3">
            <div className="flex shrink-0 flex-col items-center">
              <div className="relative flex h-20 w-20 items-center justify-center">
                <svg
                  className="h-full w-full"
                  fill="none"
                  viewBox="0 0 100 90"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path d="M22 65L42 20L53 43L37 65H22Z" fill="#788694" />
                  <path d="M50 18L72 65H57L46 43L50 18Z" fill="#7eb338" />
                  <path d="M60 47L70 65H85L68 33L60 47Z" fill="#9ecf43" />
                </svg>
              </div>
              <div className="text-center text-sm leading-tight font-bold tracking-wider text-[#6ca434]">
                {company.brandName}
              </div>
              <div className="text-[10px] leading-none tracking-widest text-gray-500 uppercase">
                {company.brandTagline}
              </div>
            </div>
            <div className="text-[13px] leading-relaxed">
              <h2 className="text-sm font-bold text-gray-900 uppercase">
                {company.legalName}
              </h2>
              <p className="text-gray-800">{company.address}</p>
              <p className="text-gray-800">
                (Tel) : {company.phone} (Fax) : {company.fax}
              </p>
              <p className="text-gray-800">Email : {company.email}</p>
            </div>
          </div>
          <div className="col-span-4 flex flex-col justify-between border-l border-gray-900 pl-3">
            <div className="text-center md:text-right">
              <h2 className="text-2xl font-bold tracking-tight text-gray-950 uppercase">
                Đơn Đặt Hàng
              </h2>
              <div className="mt-2 space-y-0.5 text-sm text-gray-900">
                <p>
                  <span className="font-bold">Số :</span>{' '}
                  <span className="text-base font-semibold">{po.code}</span>
                </p>
                <p>
                  <span className="font-semibold">Ngày :</span>{' '}
                  {formatDateVi(po.createdAt)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Supplier + delivery */}
        <div className="mt-2 text-[13.5px] leading-tight">
          <div className="flex flex-row items-start justify-between gap-3">
            <div className="max-w-[62%] space-y-0.5">
              <p className="mt-1">
                <span className="text-gray-700 italic">Kính gửi :</span>
              </p>
              <p className="text-sm font-bold text-gray-900 uppercase">
                {supplier?.name ?? po.supplierId}
              </p>
              <p className="text-gray-800">
                {supplier?.address || '—'}
              </p>
              <p className="text-gray-800">
                Người liên hệ :{' '}
                {[supplier?.phone, supplier?.contact_person].filter(Boolean).join(' ') ||
                  '—'}
              </p>
              <p className="text-gray-800">
                Số Điện thoại: {supplier?.phone || '—'}
              </p>
            </div>
            <div className="flex min-w-[260px] flex-col items-end justify-center self-auto">
              <div className="w-full border border-yellow-300 bg-[#fff000] px-6 py-1.5 text-center text-base font-extrabold tracking-wider text-gray-900 uppercase shadow-sm">
                {po.deliverySite || company.defaultSiteBanner}
              </div>
              <div className="mt-1 flex w-full items-center justify-between text-sm font-bold text-gray-900">
                <span className="text-base font-bold">Ngày giao hàng :</span>
                <span className="text-lg font-black text-slate-900">
                  {formatDateVi(po.deliveryDate)}
                </span>
              </div>
            </div>
          </div>
          <p className="mt-2 font-medium text-gray-900 italic">
            Chúng tôi cần đặt một số mặt hàng của quý Công ty như sau :
          </p>
        </div>

        {/* Lines table — cột chuẩn only; TODO cột phụ theo loại hàng */}
        <div className="mt-3 overflow-x-auto">
          {po.kind === 'FREE' ? (
            <table className="po-print-table po-free-print-table table-fixed w-full border-collapse text-xs font-medium text-gray-900" data-testid="po-print-free-table">
              <colgroup>
                <col style={{ width: '4%' }} />
                {(Array.isArray(po.freeLayout) ? po.freeLayout : []).map((column: any) => (
                  <col key={column.key} style={{ width: column.key === 'name' ? '26%' : column.key === 'note' ? '16%' : column.key === 'line_total' ? '14%' : column.key === 'unit_price' ? '12%' : column.type === 'number' ? '10%' : '8%' }} />
                ))}
              </colgroup>
              <thead><tr className="bg-gray-50"><th>Stt</th>{(Array.isArray(po.freeLayout) ? po.freeLayout : []).map((column: any) => <th key={column.key}>{column.label}</th>)}</tr></thead>
              <tbody>
                {(Array.isArray(po.freeLines) ? po.freeLines : []).map((row: any, index: number) => <tr key={row.id || index}><td className="text-center">{index + 1}</td>{(Array.isArray(po.freeLayout) ? po.freeLayout : []).map((column: any) => <td key={column.key} className={`${column.type === 'number' || column.type === 'currency' ? 'text-right' : ''}${column.type === 'currency' ? ' po-free-amount' : ''}`}>{formatFreePrintValue(row, column)}</td>)}</tr>)}
                {(() => { const layout = Array.isArray(po.freeLayout) ? po.freeLayout : []; const totalIndex = Math.max(0, layout.findIndex((column: any) => column.key === 'line_total')); const labelSpan = totalIndex + 1; return <>
                  <tr className="font-bold"><td colSpan={labelSpan} className="text-center font-bold">TỔNG CỘNG</td><td className="po-free-amount text-right font-bold">{money(vat.subtotal)}</td><td colSpan={Math.max(0, layout.length - totalIndex - 1)} /></tr>
                  <tr className="bg-[#fff000] font-bold"><td colSpan={labelSpan} className="text-center font-bold">VAT {vat.headerRate ?? 0}%</td><td className="po-free-amount text-right font-bold">{money(vat.knownVatTotal)}</td><td colSpan={Math.max(0, layout.length - totalIndex - 1)} /></tr>
                  <tr className="bg-[#fff000] font-black"><td colSpan={labelSpan} className="text-center font-black">TỔNG THANH TOÁN</td><td className="po-free-amount text-right font-black">{money(vat.payableKnown)}</td><td colSpan={Math.max(0, layout.length - totalIndex - 1)} /></tr>
                </> })()}
              </tbody>
            </table>
          ) : null}
          <table className={`po-print-table table-fixed w-full border-collapse text-xs text-gray-900${po.kind === 'FREE' ? ' hidden' : ''}`}>
            <colgroup data-layout={showTraceColumns ? 'with-trace' : 'standard'}>
              {printColumns.map((column) => (
                <col
                  key={column.key}
                  data-column={column.key}
                  style={{ width: column.width, ...(column.minWidth ? { minWidth: column.minWidth } : {}) }}
                />
              ))}
            </colgroup>
            <thead>
              <tr className="bg-gray-50">
                <th className="w-12">Stt</th>
                {showTraceColumns && <><th>Mã hàng</th><th>Màu SP</th><th>Kết cấu</th></>}
                <th>Tên sản phẩm</th>
                <th className="w-48">Quy cách</th>
                <th className="w-24">Màu</th>
                <th className="w-16">Đvt</th>
                <th className="w-20">
                  Số
                  <br />
                  lượng
                </th>
                <th className="w-28">
                  Đơn giá
                  <br />({po.currency})
                </th>
                <th className="w-32">
                  Thành tiền
                  <br />({po.currency})
                </th>
                <th className="w-32">Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((line, idx) => (
                <tr key={line.id}>
                  <td className="text-center font-medium">{idx + 1}</td>
                  {showTraceColumns && <>
                    <td className="whitespace-nowrap">{traceByPoLineId?.get(line.id)?.productCode || ''}</td>
                    <td className="whitespace-nowrap">{traceByPoLineId?.get(line.id)?.productColor || ''}</td>
                    <td className="whitespace-nowrap">{traceByPoLineId?.get(line.id)?.assembly || ''}</td>
                  </>}
                  <td className="min-w-[220px] font-bold text-gray-900 uppercase">
                    {line.itemName}
                  </td>
                  <td className="text-left">
                    {formatQc(line)}
                  </td>
                  <td className="whitespace-nowrap">{formatMaterialColor(line) === '—' ? '' : formatMaterialColor(line)}</td>
                  <td className="whitespace-nowrap text-center">{line.uom}</td>
                  <td className="text-right font-medium">{formatVnNumber(line.qty)}</td>
                  <td className="text-right font-mono">
                    {money(line.unitPriceActual)}
                    {((line.priceBasisQty ?? 1) !== 1 || (line.priceUom && line.priceUom !== line.uom)) && <div className="text-[11px] font-normal">/ {line.priceBasisQty ?? 1} {line.priceUom || line.uom}</div>}
                  </td>
                  <td className="text-right font-mono font-semibold">
                    {money(line.lineTotal)}
                  </td>
                  <td className="text-gray-800">
                    {line.manualReason?.trim() ?? ''}
                  </td>
                </tr>
              ))}

              <tr className="font-bold">
                <td className="border-r-0" />
                <td colSpan={colCount - 5} className="text-left font-bold text-gray-950">Tổng cộng</td>
                <td className="text-right font-bold text-gray-950">
                  {formatVnNumber(vat.qtyTotal)}
                </td>
                <td />
                <td className="text-right font-mono font-bold text-gray-950">
                  {money(vat.subtotal)}
                </td>
                <td />
              </tr>

              {vat.knownBuckets.map((b) => (
                <tr
                  key={`vat-${b.rate}`}
                  className="bg-[#fff000] font-bold text-gray-950"
                >
                  <td
                    className="py-1.5 text-center font-bold tracking-wider uppercase"
                    colSpan={colCount - 2}
                  >
                    VAT {b.rate}%
                    {vat.knownBuckets.length > 1
                      ? ` (${b.lineCount} dòng)`
                      : ''}
                  </td>
                  <td className="py-1.5 text-right font-mono font-bold text-black">
                    {money(b.vatAmount)}
                  </td>
                  <td />
                </tr>
              ))}

              {vat.unknownLineCount > 0 ? (
                <tr className="bg-amber-100 font-bold text-amber-950">
                  <td
                    className="py-1.5 text-center tracking-wide"
                    colSpan={colCount - 2}
                  >
                    VAT: Chưa xác định ({vat.unknownLineCount} dòng)
                  </td>
                  <td className="py-1.5 text-right font-mono">—</td>
                  <td />
                </tr>
              ) : null}

              <tr className="bg-[#fff000] font-black text-gray-950">
                <td
                  className="py-1.5 text-center text-[13px] font-black tracking-wider uppercase"
                  colSpan={colCount - 2}
                >
                  Tổng thanh toán
                  {vat.unknownLineCount > 0 ? ' *' : ''}
                </td>
                <td className="py-1.5 text-right font-mono text-sm font-black text-black">
                  {money(vat.payableKnown)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
          {vat.unknownLineCount > 0 ? (
            <p className="mt-2 text-[11px] text-amber-800 italic">
              * Tổng thanh toán trên chưa bao gồm VAT của {vat.unknownLineCount}{' '}
              dòng chưa xác định mức thuế — không tính UNKNOWN như 0%.
            </p>
          ) : null}
        </div>

        <div className="mt-2 text-xs font-medium text-gray-900 po-print-notes">
          <div className="flex items-center gap-1 text-sm font-bold text-gray-950">
            <span>※</span>
            <span>LƯU Ý</span>
          </div>
          <p className="mt-1 font-bold">
            Liên hệ :{' '}
            <span className="text-sm font-extrabold text-blue-900">
              {company.purchasingContactNote}
            </span>
          </p>
          {po.note ? (
            <p className="mt-1 text-[11px] text-gray-600 italic">{po.note}</p>
          ) : null}
        </div>

        {/* Signatures — names empty unless approvedBy maps to role */}
        <section className="mt-2 pt-1">
          <div className="grid gap-2 text-center text-xs po-print-signatures" style={{ gridTemplateColumns: `repeat(${signers.length}, minmax(0, 1fr))` }}>
            {signers.map((signer, index) => <SignatureSlot key={index} title={signer.title} signer={signer.name ? { name: signer.name, note: '' } : null} placeholder="[Chữ ký / họ tên]" />)}
          </div>
        </section>
      </main>
    </div>
  )
}

function SignatureSlot({
  title,
  signer,
  placeholder,
  highlight,
}: {
  title: ReactNode
  signer: { name: string; note: string } | null
  placeholder: string
  highlight?: boolean
}) {
  return (
    <div className="flex h-24 flex-col justify-between">
      <div className="leading-tight font-bold tracking-tight text-gray-900 uppercase">
        {title}
      </div>
      <div className="relative mt-auto">
        {highlight && signer ? (
          <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 rotate-[-6deg] rounded border border-emerald-600 px-1.5 py-0.5 font-mono text-[9px] tracking-tighter text-emerald-700 uppercase opacity-80">
            APPROVED
          </div>
        ) : null}
        {signer ? (
          <>
            <div className="text-[13px] font-bold text-gray-900 uppercase">
              {signer.name}
            </div>
            <div className="text-[10px] text-emerald-700 italic">
              {signer.note}
            </div>
          </>
        ) : (
          <>
            <div className="h-8 border-b border-dashed border-gray-300" />
            <div className="no-print mt-1 text-[10px] text-gray-400 italic">
              {placeholder}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/** Route page: load PO from API. */
export function PoPrintPage() {
  const { poId = '' } = useParams()
  const navigate = useNavigate()
  const [poDto, setPoDto] = useState<PurchaseOrderDto | null>(null)
  const [supplier, setSupplier] = useState<Supplier | null>(null)
  const [materials, setMaterials] = useState<Material[]>([])
  const [traceByPoLineId, setTraceByPoLineId] = useState<Map<string, PoPrintTrace>>(new Map())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const p = await getPurchaseOrder(poId)
        if (!p) throw new Error('Không tìm thấy PO')
        setMaterials(await materialService.list())
        setPoDto(p)
        const traceLines = (p?.lines ?? []).filter((line) => line.requirement_line_id)
        if (traceLines.length) {
          const traceResults = (await Promise.all(traceLines.map((line) =>
            getPoLineTraceabilityApi(line.id).catch(() => null),
          ))).filter((item): item is PoLineTraceabilityResult => item !== null)
          const batchIds = [...new Set(traceResults.map((item) => item.batch_id))]
          const batches = (await Promise.all(batchIds.map((id) => getMaterialRequirementBatchApi(id).catch(() => null))))
            .filter((batch): batch is MaterialRequirementBatchRow => batch !== null)
          setTraceByPoLineId(buildPoPrintTraceByLineId(poDtoToUi(p).lines, traceResults, batches))
        } else setTraceByPoLineId(new Map())
        if (p?.supplier_id) {
          const sup = await supplierService.getById(p.supplier_id)
          setSupplier(sup as any)
        }
      } catch {
        setPoDto(null)
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [poId])

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Đang tải đơn hàng...</div>
  }

  if (!poDto) {
    return (
      <EmptyState
        title="Không tìm thấy PO"
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/po')}>
            Quay lại
          </Button>
        }
      />
    )
  }

  const uiPo = poDtoToUi(poDto, materials)
  return <PoPrintView po={uiPo} supplier={supplier} traceByPoLineId={traceByPoLineId} />
}
